
import { CreateSubjectPersonGroupSchema, UpdateSubjectPersonGroupSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { logDropdownParamsInvocation } from '../utils/dropdown-params-telemetry.js';
import { withRetry } from '../utils/retry.js';

export class SubjectPersonGroupService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                pagination: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'SubjectPersonGroup listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch subject-person-group list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch subject-person-group');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        try {
            // Validation
            const validatedData = CreateSubjectPersonGroupSchema.parse(data);

            const response = await this.api.create(validatedData);

            return {
                success: true,
                data: response.data,
                message: response.message || 'SubjectPersonGroup oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create subject-person-group');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        try {
            // Validation
            const validatedData = UpdateSubjectPersonGroupSchema.partial().parse(data);

            const response = await this.api.update(id, validatedData);

            return {
                success: true,
                data: response.data,
                message: response.message || 'SubjectPersonGroup güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update subject-person-group');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'SubjectPersonGroup silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete subject-person-group');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            // Fallback to sequential
            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }

            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} öğe silindi`
            };
        }
    }

    /**
     * Get dropdown parameters
     * @returns {Promise<Object>}
     */
    async getDropdownParams() {
        logDropdownParamsInvocation('SubjectPersonGroupService');
        try {
            const response = await this.api.getDropdown();
            return {
                success: true,
                data: response.data || [],
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch dropdown params');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error.name === 'ZodError') {
            throw new ApiError('Validation Error', 400, 'VALIDATION_ERROR', error.errors);
        }

        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
